import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiBell, FiSend, FiCheck } from 'react-icons/fi';

const authH = token => ({ headers: { Authorization: `Bearer ${token}` } });
const fmt = iso => iso ? new Date(iso).toLocaleString() : '—';

export default function AdminNotifications({ token }) {
  const campaignId = localStorage.getItem('selectedCampaign') || 'medicare';
  const [agents, setAgents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [message, setMessage]   = useState('');
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter]     = useState({ status: 'all', search: '' });
  const [history, setHistory]   = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [tab, setTab]           = useState('send');
  const [modAlerts, setModAlerts]   = useState([]);
  const [modLoading, setModLoading] = useState(false);
  const [modFilter, setModFilter]   = useState('all'); // 'all' | 'confirmed' | 'possible'
  const [modSearch, setModSearch]   = useState('');

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/admin/agents?campaignId=${campaignId}`, authH(token));
      const flat = [];
      for (const a of (r.data || [])) {
        if (a.agentType === 'group') {
          for (const m of (a.groupMembers || [])) {
            if (m.email) flat.push({ email: m.email.toLowerCase(), name: m.name || m.email, enabled: m.enabled !== false, group: a.ghlLocationName || '' });
          }
        } else if (a.email) {
          flat.push({ email: a.email.toLowerCase(), name: a.name || a.email, enabled: a.distributionConfig?.enabled !== false, group: null });
        }
      }
      setAgents(flat);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  }, [token, campaignId]);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  const loadHistory = async () => {
    setHistLoading(true);
    try { const r = await axios.get('/api/admin-notifications/history?limit=200', authH(token)); setHistory(r.data.notifications || []); }
    catch(e) { console.error(e); } finally { setHistLoading(false); }
  };
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab]);

  const loadModAlerts = async () => {
    setModLoading(true);
    try {
      const r = await axios.get('/api/admin-notifications/history?limit=500', authH(token));
      const all = r.data.notifications || [];
      setModAlerts(all.filter(n => n.moderationAlert).sort((a,b) => (b.createdAt||'').localeCompare(a.createdAt||'')));
    } catch(e) { console.error(e); } finally { setModLoading(false); }
  };
  useEffect(() => { if (tab === 'moderation') loadModAlerts(); }, [tab]);

  const filtered = agents.filter(a => {
    if (filter.status === 'active' && !a.enabled) return false;
    if (filter.status === 'inactive' && a.enabled) return false;
    if (filter.search) { const q=filter.search.toLowerCase(); if(!a.email.includes(q)&&!(a.name||'').toLowerCase().includes(q)) return false; }
    return true;
  });

  const toggleAgent = e => setSelected(p => { const n=new Set(p); n.has(e)?n.delete(e):n.add(e); return n; });
  const selectAll   = () => setSelected(new Set(filtered.map(a=>a.email)));
  const selectActive= () => setSelected(new Set(filtered.filter(a=>a.enabled).map(a=>a.email)));
  const selectInactive=()=> setSelected(new Set(filtered.filter(a=>!a.enabled).map(a=>a.email)));
  const clearSel    = () => setSelected(new Set());
  const isAllSel    = filtered.length > 0 && filtered.every(a => selected.has(a.email));

  const handleSend = async () => {
    if (!message.trim()) return alert('Please enter a message');
    if (selected.size === 0) return alert('Please select at least one agent');
    setSending(true); setSent(null);
    try {
      const r = await axios.post('/api/admin-notifications/send', { message: message.trim(), agentEmails: [...selected], campaignId }, authH(token));
      setSent(r.data); setMessage(''); setSelected(new Set());
    } catch(e) { alert(e.response?.data?.error || 'Send failed'); } finally { setSending(false); }
  };

  const tabBtn = (t, label) => (
    <button onClick={() => setTab(t)} style={{ padding:'8px 20px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:14, background:tab===t?'#1a56db':'#f3f4f6', color:tab===t?'#fff':'#374151' }}>{label}</button>
  );

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
        <FiBell size={22} color="#1a56db"/>
        <h1 style={{ margin:0, fontSize:22, fontWeight:700 }}>Agent Notifications</h1>
      </div>
      <p style={{ color:'#6b7280', marginBottom:20 }}>Broadcast notifications to agents. They appear as a moving banner on their dashboard and on their notifications page.</p>
      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        {tabBtn('send','📤 Send Notification')}
        {tabBtn('history','📋 History')}
        {tabBtn('moderation', `🛡️ Moderation Alerts${modAlerts.length > 0 ? ` (${modAlerts.filter(a=>!a.readAt).length})` : ''}`)}
      </div>
      {tab==='send'&&(
        <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:24, alignItems:'start' }}>
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid #f3f4f6', display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <input value={filter.search} onChange={e=>setFilter(p=>({...p,search:e.target.value}))} placeholder="Search agents…" style={{ flex:1, minWidth:150, padding:'7px 12px', border:'1px solid #d1d5db', borderRadius:7, fontSize:13 }}/>
              <select value={filter.status} onChange={e=>setFilter(p=>({...p,status:e.target.value}))} style={{ padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:7, fontSize:13 }}>
                <option value="all">All agents</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
              </select>
            </div>
            <div style={{ padding:'10px 16px', borderBottom:'1px solid #f3f4f6', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:12, color:'#6b7280', fontWeight:600 }}>Quick select:</span>
              <button onClick={selectAll} style={{ fontSize:12, padding:'4px 10px', borderRadius:6, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer' }}>All ({filtered.length})</button>
              <button onClick={selectActive} style={{ fontSize:12, padding:'4px 10px', borderRadius:6, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer' }}>Active ({filtered.filter(a=>a.enabled).length})</button>
              <button onClick={selectInactive} style={{ fontSize:12, padding:'4px 10px', borderRadius:6, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer' }}>Inactive ({filtered.filter(a=>!a.enabled).length})</button>
              {selected.size>0&&<button onClick={clearSel} style={{ fontSize:12, padding:'4px 10px', borderRadius:6, border:'1px solid #fee2e2', background:'#fef2f2', color:'#dc2626', cursor:'pointer' }}>Clear ({selected.size})</button>}
            </div>
            <div style={{ padding:'8px 16px', background:'#f9fafb', display:'grid', gridTemplateColumns:'28px 1fr 80px', gap:10, alignItems:'center', borderBottom:'1px solid #f3f4f6' }}>
              <input type="checkbox" checked={isAllSel} onChange={isAllSel?clearSel:selectAll} style={{ cursor:'pointer' }}/>
              <span style={{ fontSize:12, fontWeight:700, color:'#6b7280', textTransform:'uppercase' }}>Agent</span>
              <span style={{ fontSize:12, fontWeight:700, color:'#6b7280', textTransform:'uppercase' }}>Status</span>
            </div>
            <div style={{ maxHeight:460, overflowY:'auto' }}>
              {loading?<div style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>Loading…</div>:
              filtered.length===0?<div style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>No agents</div>:
              filtered.map(a=>(
                <div key={a.email} onClick={()=>toggleAgent(a.email)} style={{ padding:'10px 16px', display:'grid', gridTemplateColumns:'28px 1fr 80px', gap:10, alignItems:'center', cursor:'pointer', background:selected.has(a.email)?'#eff6ff':'#fff', borderBottom:'1px solid #f9fafb' }}>
                  <input type="checkbox" checked={selected.has(a.email)} onChange={()=>toggleAgent(a.email)} onClick={e=>e.stopPropagation()} style={{ cursor:'pointer' }}/>
                  <div><div style={{ fontSize:13, fontWeight:600, color:'#111827' }}>{a.name}</div><div style={{ fontSize:11, color:'#9ca3af' }}>{a.email}{a.group?` · ${a.group}`:''}</div></div>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:600, background:a.enabled?'#dcfce7':'#fee2e2', color:a.enabled?'#16a34a':'#dc2626' }}>{a.enabled?'Active':'Inactive'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Compose panel */}
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:20 }}>
            <h3 style={{ margin:'0 0 14px', fontSize:16, fontWeight:700 }}>Compose</h3>
            <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:13 }}><strong>{selected.size}</strong> agent{selected.size!==1?'s':''} selected</div>
            <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Type your notification…" rows={5} style={{ width:'100%', padding:'10px 12px', border:'1px solid #d1d5db', borderRadius:8, fontSize:14, resize:'vertical', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}/>
            <div style={{ fontSize:11, color:'#9ca3af', textAlign:'right', marginTop:2, marginBottom:8 }}>{message.length}/500</div>
            {message.trim()&&<div style={{ marginBottom:12, padding:'10px 14px', background:'#fef9c3', border:'1px solid #fde68a', borderRadius:8, fontSize:13, color:'#713f12' }}><div style={{ fontSize:11, fontWeight:700, marginBottom:4 }}>PREVIEW (marquee)</div>{message.trim()}</div>}
            <button onClick={handleSend} disabled={sending||selected.size===0||!message.trim()} style={{ width:'100%', padding:'12px', background:sending||selected.size===0||!message.trim()?'#9ca3af':'#1a56db', color:'#fff', border:'none', borderRadius:10, fontSize:15, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <FiSend size={16}/>{sending?'Sending…':`Send to ${selected.size} agent${selected.size!==1?'s':''}`}
            </button>
            {sent&&<div style={{ marginTop:12, padding:'10px 14px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, fontSize:13, color:'#15803d', display:'flex', alignItems:'center', gap:8 }}><FiCheck size={14}/> Sent to <strong>{sent.sent}</strong> agents!</div>}
          </div>
        </div>
      )}
      {tab==='history'&&(
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
          <div style={{ padding:'14px 16px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontWeight:600 }}>Sent ({history.length})</span>
            <button onClick={loadHistory} style={{ fontSize:13, padding:'6px 14px', borderRadius:7, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer' }}>↺ Refresh</button>
          </div>
          {histLoading?<div style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>Loading…</div>:
          history.length===0?<div style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>No notifications sent yet</div>:(
            <div style={{ maxHeight:600, overflowY:'auto' }}>
              {history.map(n=>(
                <div key={n.notificationId} style={{ padding:'12px 16px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <div style={{ flex:1 }}><div style={{ fontSize:14, color:'#111827', marginBottom:4 }}>{n.message}</div><div style={{ fontSize:11, color:'#9ca3af' }}>To: {n.agentEmail} · {new Date(n.createdAt).toLocaleString()} · by {n.sentBy}</div></div>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:600, flexShrink:0, background:n.readAt?'#f3f4f6':'#eff6ff', color:n.readAt?'#6b7280':'#1a56db' }}>{n.readAt?'Read':'Unread'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab==='moderation'&&(()=>{
        const filtered = modAlerts.filter(n => {
          if (modFilter === 'confirmed' && n.alertType !== 'signup_source_confirmed') return false;
          if (modFilter === 'possible'  && n.alertType !== 'signup_source_possible')  return false;
          if (modFilter === 'unread'    && n.readAt) return false;
          if (modSearch.trim()) {
            const q = modSearch.toLowerCase();
            if (!(n.agentEmailRef||'').toLowerCase().includes(q) &&
                !(n.leadEmailRef||'').toLowerCase().includes(q)  &&
                !(n.safeAdminSummary||'').toLowerCase().includes(q)) return false;
          }
          return true;
        });
        const counts = {
          all: modAlerts.length,
          confirmed: modAlerts.filter(n=>n.alertType==='signup_source_confirmed').length,
          possible:  modAlerts.filter(n=>n.alertType==='signup_source_possible').length,
          unread:    modAlerts.filter(n=>!n.readAt).length,
        };
        const fBtn = (key, label, color) => (
          <button onClick={()=>setModFilter(key)} style={{ padding:'5px 14px', borderRadius:20, border:`1.5px solid ${modFilter===key?color:'#e5e7eb'}`, background:modFilter===key?color:'#fff', color:modFilter===key?'#fff':'#374151', fontSize:12, fontWeight:600, cursor:'pointer' }}>
            {label} ({counts[key]})
          </button>
        );
        return (
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
            <div style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
              <div>
                <span style={{ fontWeight:700, fontSize:15 }}>🛡️ Moderation Alerts</span>
                <span style={{ marginLeft:10, fontSize:12, color:'#6b7280' }}>Signup-source requests &amp; chat safety events</span>
              </div>
              <button onClick={loadModAlerts} style={{ fontSize:13, padding:'6px 14px', borderRadius:7, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer' }}>↺ Refresh</button>
            </div>
            <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', background:'#fafafa', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              {fBtn('all','All','#1a56db')}
              {fBtn('confirmed','🔴 Confirmed','#dc2626')}
              {fBtn('possible','🟡 Possible','#d97706')}
              {fBtn('unread','🔵 Unread','#0891b2')}
              <input value={modSearch} onChange={e=>setModSearch(e.target.value)} placeholder="Search agent / lead email…"
                style={{ marginLeft:'auto', padding:'6px 12px', borderRadius:8, border:'1px solid #d1d5db', fontSize:13, width:220, outline:'none' }}/>
            </div>
            <div style={{ padding:'10px 18px', borderBottom:'1px solid #f3f4f6', display:'flex', gap:24, fontSize:13 }}>
              <span>Total: <strong>{counts.all}</strong></span>
              <span style={{ color:'#dc2626' }}>🔴 Confirmed: <strong>{counts.confirmed}</strong></span>
              <span style={{ color:'#d97706' }}>🟡 Possible: <strong>{counts.possible}</strong></span>
              <span style={{ color:'#0891b2' }}>🔵 Unread: <strong>{counts.unread}</strong></span>
            </div>

            {modLoading ? (
              <div style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>
                {modAlerts.length === 0 ? '✅ No moderation alerts yet' : '🔍 No alerts match your filter'}
              </div>
            ) : (
              <div style={{ maxHeight:680, overflowY:'auto' }}>
                {filtered.map(n => {
                  const isConf = n.alertType === 'signup_source_confirmed';
                  const isPoss = n.alertType === 'signup_source_possible';
                  const bgColor     = isConf ? '#fef2f2' : isPoss ? '#fffbeb' : '#f0f9ff';
                  const borderColor = isConf ? '#dc2626'  : isPoss ? '#d97706'  : '#2563eb';
                  const badge = isConf ? { bg:'#dc2626', label:'🔴 Confirmed — Signup-Source Request' }
                              : isPoss ? { bg:'#d97706', label:'🟡 Possible — Signup-Source Request' }
                              :          { bg:'#2563eb', label:'ℹ️ Moderation Event' };
                  return (
                    <div key={n.notificationId} style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6', background:bgColor, borderLeft:`4px solid ${borderColor}` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:20, background:badge.bg, color:'#fff' }}>{badge.label}</span>
                          {!n.readAt && <span style={{ width:8, height:8, borderRadius:'50%', background:'#2563eb', display:'inline-block' }} title="Unread"/>}
                        </div>
                        <span style={{ fontSize:12, color:'#9ca3af' }}>{new Date(n.createdAt).toLocaleString()}</span>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 24px', fontSize:13 }}>
                        {n.agentEmailRef && <div><span style={{ color:'#6b7280' }}>Agent: </span><strong>{n.agentEmailRef}</strong></div>}
                        {n.leadEmailRef  && <div><span style={{ color:'#6b7280' }}>Lead: </span><strong>{n.leadEmailRef}</strong></div>}
                        {n.conversationIdRef && (
                          <div style={{ gridColumn:'1/-1' }}>
                            <span style={{ color:'#6b7280' }}>Conversation: </span>
                            <code style={{ fontSize:12, background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{n.conversationIdRef}</code>
                          </div>
                        )}
                        {n.safeAdminSummary && (
                          <div style={{ gridColumn:'1/-1', marginTop:6, padding:'8px 12px', background:'rgba(0,0,0,0.04)', borderRadius:6, fontStyle:'italic', color:'#374151' }}>
                            💬 "{n.safeAdminSummary}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

